import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './styles.css';

gsap.registerPlugin(ScrollTrigger);

const APP_ORIGIN = String(import.meta.env.VITE_INEDITA_APP_ORIGIN || 'https://inedito.schlapp.com.br').replace(/\/$/, '');
const visualRoot = '/visuals/biomorphic';
const journeyVideoSrc = String(import.meta.env.VITE_INEDITA_JOURNEY_VIDEO_SRC || '').trim();
const journeyPosterSrc = String(import.meta.env.VITE_INEDITA_JOURNEY_POSTER_SRC || '').trim();

const image = (name) => `${visualRoot}/${name}.webp`;
const appPath = (path) => `${APP_ORIGIN}${path}`;
const startHref = appPath('/entrar?next=%2Fapp');
const checkoutHref = appPath('/entrar?next=%2Fcheckout');
const applyHref = appPath('/entrar?next=%2Fapoio');

const bioAssets = {
  hero: { src: image('BIOMORPHIC-01-novo-gargalo'), alt: 'Estrutura biomórfica transparente envolve um núcleo luminoso entre formas escuras.' },
  vocabulary: { src: image('BIOMORPHIC-03-mergulho'), alt: 'Camadas translúcidas e linhas de luz se aprofundam em uma estrutura escura, evocando uma investigação em andamento.' },
  method: { src: image('BIOMORPHIC-04-cartografia'), alt: 'Fragmentos luminosos se conectam por linhas finas até formar uma configuração única sobre um campo escuro.' },
  questions: { src: image('BIOMORPHIC-02-perguntas'), alt: 'Instrumentos ópticos luminosos aparecem dentro de uma forma biomórfica escura, sugerindo investigação cuidadosa.' },
  extraction: { src: image('BIOMORPHIC-05-extracao'), alt: 'Um núcleo verde-azulado se destaca dentro de uma estrutura orgânica escura, como um sinal extraído de muitos fragmentos.' },
  council: { src: image('BIOMORPHIC-06-conselho'), alt: 'Lentes e estruturas circulares examinam um núcleo luminoso sob diferentes ângulos em um ambiente escuro.' },
  reality: { src: image('BIOMORPHIC-07-realidade'), alt: 'Um núcleo luminoso atravessa formas de pressão e equilíbrio, sugerindo uma hipótese confrontada com a realidade.' },
  reveal: { src: image('BIOMORPHIC-08-revelacao'), alt: 'Uma forma luminosa é revelada dentro de uma estrutura biomórfica, sugerindo uma combinação pessoal compreendida.' },
  execution: { src: image('BIOMORPHIC-09-execucao'), alt: 'Um núcleo central se desdobra em caminhos organizados dentro de uma estrutura escura, sugerindo opções de execução.' },
  human: { src: image('BIOMORPHIC-10-estrategia-humana'), alt: 'Uma passagem entre instrumento de precisão e dossiê humano representa a transição para uma decisão acompanhada.' },
};

const creationWords = ['sites', 'aplicativos', 'vídeos', 'marcas', 'automações', 'conteúdo', 'negócios'];
const HERO_HEADLINE = 'Se hoje em dia podemos criar qualquer coisa, o problema passa a ser: o que vale a pena criar.';
const HERO_TAIL = 'o problema passa a ser: o que vale a pena criar.';

const publicOffers = [
  {
    price: 'R$297',
    name: 'INÉDITA - DESCOBERTA',
    copy: 'Descubra o que você poderia criar.',
    cta: 'Descobrir minhas oportunidades',
    includes: [
      'Investigação pelo Método INÉDITA',
      'Aprofundamentos quando necessários',
      'Análise e cruzamento das respostas',
      'Passagem pelo Conselho',
      'Múltiplas possibilidades personalizadas',
      'Recomendação principal e alternativas relevantes',
      'Racional da descoberta e próximos passos iniciais',
    ],
  },
  {
    price: 'R$997',
    name: 'INÉDITA - EXECUÇÃO',
    copy: 'Não descubra apenas o que fazer. Descubra como fazer.',
    cta: 'Quero a ideia + plano',
    includes: [
      'Tudo da INÉDITA - Descoberta',
      'Plano Personalizado de Execução',
      'Ferramentas, plataformas e recursos adequados',
      'Workflow e sequência operacional',
      'Custos iniciais, riscos e o que evitar',
      'Validação inicial da oportunidade',
    ],
  },
  {
    price: 'R$1.497',
    name: 'INÉDITA - ESTRATÉGIA',
    copy: 'Chegue à conversa com a investigação e o plano já estruturados.',
    cta: 'Quero o Método + sessão estratégica',
    includes: [
      'Tudo da INÉDITA - Descoberta',
      'Plano Personalizado de Execução',
      'Sessão estratégica individual de aproximadamente 1 hora',
      'Questionamento de premissas e priorização',
      'Ajuste de direção, alternativas e obstáculos',
      'Definição dos próximos movimentos',
    ],
  },
];

const salesAssets = {
  hero: { src: '/visuals/sales/hero-seed.webp', alt: 'Gota de luz verde-azul suspensa no escuro, com fios de conexão ao redor.' },
  method: { src: '/visuals/sales/method-map.webp', alt: 'Constelação de fragmentos luminosos ligados por fios, formando um mapa único.' },
  paths: { src: '/visuals/sales/three-paths.webp', alt: 'Uma semente de luz no centro de um chão escuro, com três caminhos se abrindo.' },
};

const biomorphicChapters = [
  ['biomorphic-hero', 'A promessa'],
  ['nova-escolha', 'O gargalo'],
  ['metodo', 'O método'],
  ['como-funciona', 'Como funciona'],
  ['investigacao', 'As perguntas'],
  ['extracao', 'Os sinais'],
  ['entregaveis', 'O que você recebe'],
  ['ofertas', 'Os caminhos'],
  ['compromissos', 'Os compromissos'],
  ['acompanhamento', 'O próximo passo'],
];

const methodStages = [
  { name: 'Mergulho', title: 'Antes de procurar a ideia, nós procuramos você.', copy: 'Experiências, habilidades, conhecimentos, problemas já resolvidos, ambientes, acessos, recursos, restrições e ambições entram na investigação. Às vezes a oportunidade está escondida em algo que você parou de enxergar.' },
  { name: 'Cartografia', title: 'Fragmentos aparentemente desconectados começam a formar um mapa.', copy: 'Uma experiência se conecta a uma habilidade. A habilidade encontra um problema. O problema encontra um mercado. Um acesso encontra um comprador. Uma restrição muda o modelo.' },
  { name: 'Extração', title: 'O que parecia comum começa a revelar valor.', copy: 'O Método procura conhecimento subestimado, problemas monetizáveis, acessos pouco explorados e combinações difíceis de copiar. O Índice de Assimetria™ mostra o quanto a oportunidade depende da sua combinação específica.' },
  { name: 'Conselho', title: 'Uma ideia não chega até você só porque parece boa.', copy: 'Cada hipótese é confrontada sob perspectivas de potencial, fragilidade, aderência, modelo de negócio e a pergunta: por que alguém pagaria, e por que justamente você deveria construir isso.' },
  { name: 'Realidade', title: 'Uma ideia fascinante ainda pode ser um péssimo negócio.', copy: 'Existe um problema real? Quem pagaria agora? Como essa pessoa seria alcançada? É possível validar antes de construir tudo? O objetivo é separar ideias interessantes de oportunidades que merecem ser exploradas.' },
  { name: 'Revelação', title: 'Você entende por que isso apareceu especificamente para você.', copy: 'Não chega um “faça isso”. Chega o racional: que parte da história importou, que capacidade se conectou, que acesso fez diferença e o que ainda precisa ser provado.' },
];

const salesFaqs = [
  { q: 'Isso é um gerador de ideias com inteligência artificial?', a: 'Não. Se a resposta pudesse ter sido produzida por alguém pedindo “me dê uma ideia de negócio” para uma ferramenta genérica, o Método teria falhado. A tecnologia apoia a investigação. O produto é o processo de descoberta.' },
  { q: 'E se ainda não existe um negócio, só um repertório?', a: 'O ponto de partida é a combinação do que você sabe, viveu, possui e consegue acessar. Não é necessário chegar com um plano pronto. É necessário chegar com respostas reais.' },
  { q: 'O que o caminho de R$297 entrega de fato?', a: 'Investigação pelo Método INÉDITA, aprofundamentos quando necessários, passagem pelo Conselho, possibilidades personalizadas, recomendação principal com alternativas relevantes, racional da descoberta e próximos passos iniciais.' },
  { q: 'Qual a diferença entre os três caminhos públicos?', a: 'Descoberta revela o que merece ser criado. Execução acrescenta o Plano Personalizado de como fazer. Estratégia inclui a descoberta, o plano e uma sessão individual de aproximadamente uma hora, já com a investigação feita.' },
  { q: 'Há garantia de faturamento ou de um negócio pronto?', a: 'Não. O trabalho garante o método, o confronto com a realidade e a entrega comprada. Não garante clientes, receita ou um negócio operando. Quando a evidência for insuficiente, o Método declara a limitação.' },
];

const extractionSignals = [
  ['Conhecimento subestimado', bioAssets.extraction],
  ['Problemas monetizáveis', bioAssets.questions],
  ['Acessos pouco explorados', bioAssets.method],
  ['Restrições reais', bioAssets.reality],
];

const lp1Signals = [
  {
    id: 'knowledge',
    number: '01',
    label: 'Conhecimento subestimado',
    summary: 'O que você sabe pode ser mais valioso do que parece.',
    detail: 'Atalhos, repertórios, leituras e práticas adquiridas na experiência podem esconder uma vantagem que ainda não foi nomeada.',
  },
  {
    id: 'problems',
    number: '02',
    label: 'Problemas monetizáveis',
    summary: 'Frustrações recorrentes apontam para dores concretas.',
    detail: 'Gargalos, desperdícios e problemas que se repetem indicam onde pode existir uma necessidade real de solução.',
  },
  {
    id: 'access',
    number: '03',
    label: 'Acessos pouco explorados',
    summary: 'O contexto ao seu redor muda o que é possível testar.',
    detail: 'Relacionamentos, canais, comunidades, infraestrutura e contextos aos quais você já chega alteram a viabilidade de uma direção.',
  },
  {
    id: 'constraints',
    number: '04',
    label: 'Restrições reais',
    summary: 'Limites bem vistos ajudam a separar possibilidade de fantasia.',
    detail: 'Tempo, capital, energia, território e limites operacionais não são ruído. Eles ajudam a escolher o que merece ser investigado.',
  },
];

const LP1_SCRAMBLE = 'merece energia?';

function useLp1Scramble(reducedMotion) {
  const [phrase, setPhrase] = useState(LP1_SCRAMBLE);
  useEffect(() => {
    if (reducedMotion) {
      setPhrase(LP1_SCRAMBLE);
      return undefined;
    }
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    let step = 0;
    let interval = 0;
    const reveal = () => {
      const locked = Math.min(LP1_SCRAMBLE.length, Math.floor((step / 13) * LP1_SCRAMBLE.length));
      setPhrase(LP1_SCRAMBLE.split('').map((character, index) => {
        if (character === ' ' || !/[a-z]/i.test(character) || index < locked) return character;
        return alphabet[(index * 9 + step * 7) % alphabet.length];
      }).join(''));
    };
    const start = window.setTimeout(() => {
      reveal();
      interval = window.setInterval(() => {
        step += 1;
        reveal();
        if (step >= 13) {
          window.clearInterval(interval);
          setPhrase(LP1_SCRAMBLE);
        }
      }, 72);
    }, 340);
    return () => { window.clearTimeout(start); if (interval) window.clearInterval(interval); };
  }, [reducedMotion]);
  return phrase;
}

function Lp1SignalExplorer() {
  const [activeId, setActiveId] = useState(lp1Signals[0].id);
  const active = lp1Signals.find((signal) => signal.id === activeId) || lp1Signals[0];
  const selectSignal = (id) => setActiveId(id);
  return <div className="lp1-signal-explorer">
    <div className="lp1-signal-explorer__menu" role="tablist" aria-label="Sinais investigados">
      {lp1Signals.map((signal) => <button
        className={active.id === signal.id ? 'is-active' : ''}
        id={`signal-tab-${signal.id}`}
        key={signal.id}
        role="tab"
        type="button"
        aria-controls={`signal-panel-${signal.id}`}
        aria-selected={active.id === signal.id}
        tabIndex={active.id === signal.id ? 0 : -1}
        onClick={() => selectSignal(signal.id)}
        onKeyDown={(event) => {
          if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;
          event.preventDefault();
          const currentIndex = lp1Signals.findIndex((item) => item.id === signal.id);
          const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
          const next = lp1Signals[(currentIndex + direction + lp1Signals.length) % lp1Signals.length];
          selectSignal(next.id);
          window.requestAnimationFrame(() => document.getElementById(`signal-tab-${next.id}`)?.focus());
        }}
      ><span>{signal.number}</span><strong>{signal.label}</strong><Arrow /></button>)}
    </div>
    <article className="lp1-signal-explorer__panel" id={`signal-panel-${active.id}`} role="tabpanel" aria-labelledby={`signal-tab-${active.id}`} tabIndex="0">
      <div className="lp1-signal-explorer__panel-meta"><span>{active.number}</span><span>o sinal em foco</span></div>
      <h3>{active.label}</h3>
      <p className="lp1-signal-explorer__summary">{active.summary}</p>
      <p>{active.detail}</p>
      <a className="lp1-text-link" href="#metodo">Ver como isso entra no método <Arrow /></a>
    </article>
  </div>;
}

function Lp1FullBleedVisual({ visual, className = '' }) {
  return <div className={`lp1-full-bleed__visual ${className}`.trim()} aria-hidden="true"><img src={visual.src} alt="" loading="lazy" decoding="async" /><span className="lp1-full-bleed__veil" /><span className="lp1-full-bleed__grain" /></div>;
}

function BrandLockup({ href = '/', className = '' }) {
  return <a className={`lab-brand ${className}`.trim()} href={href} aria-label="INÉDITA Labs, página inicial"><span className="lab-brand__mark" aria-hidden="true"><img src="/brand/inedito-gota-semente-alpha.webp" alt="" /></span><span>INÉDITA <small>LABS</small></span></a>;
}

function Header({ variant = 'dark', label = 'Landing Lab', publicSurface = false, brandHref = publicSurface ? '/biomorphic' : '/' }) {
  return (
    <header className={`lab-header lab-header--${variant} ${publicSurface ? 'lab-header--public' : ''}`}>
      <BrandLockup href={brandHref} />
      {!publicSurface ? <span className="lab-header__label">{label}</span> : null}
      {!publicSurface ? <nav className="lab-nav" aria-label="Variações da landing page"><a href="/biomorphic">Biomorphic</a><a href="/business">Empresas</a><a href="/journey">Jornada</a></nav> : null}
      <a className="text-link" href={startHref}>Começar</a>
    </header>
  );
}

function Arrow() {
  return <span className="arrow" aria-hidden="true"><svg viewBox="0 0 16 16" focusable="false"><path d="M3 13 13 3M5 3h8v8" /></svg></span>;
}

function CheckMark() {
  return <span className="check-mark" aria-hidden="true"><svg viewBox="0 0 16 16" focusable="false"><path d="m3 8.5 3.1 3.1L13 4.8" /></svg></span>;
}

function CTA({ href, children, tone = 'dark', magnetic = false }) {
  const resetMagnet = (event) => {
    if (!magnetic) return;
    event.currentTarget.style.setProperty('--magnetic-x', '0px');
    event.currentTarget.style.setProperty('--magnetic-y', '0px');
  };
  const moveMagnet = (event) => {
    if (!magnetic || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width - .5) * 12;
    const y = ((event.clientY - bounds.top) / bounds.height - .5) * 8;
    event.currentTarget.style.setProperty('--magnetic-x', `${x.toFixed(2)}px`);
    event.currentTarget.style.setProperty('--magnetic-y', `${y.toFixed(2)}px`);
  };
  return <a className={`cta cta--${tone} ${magnetic ? 'cta--magnetic' : ''}`.trim()} href={href} onPointerMove={moveMagnet} onPointerLeave={resetMagnet}>{children}<Arrow /></a>;
}

function Figure({ src, alt, className = '', priority = false }) {
  return <figure className={`image-figure ${className}`.trim()}><img src={src} alt={alt} loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" /><span aria-hidden="true" /></figure>;
}

function Footer({ variant = 'dark' }) {
  return <footer className={`lab-footer lab-footer--${variant}`}><BrandLockup /><span>Observar. Decidir. Agir. Medir. Aprender. Adaptar.</span><a href={applyHref}>Acompanhamento por aplicação <Arrow /></a></footer>;
}

function useReducedMotionPreference() {
  const [reducedMotion, setReducedMotion] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = (event) => setReducedMotion(event.matches);
    media.addEventListener?.('change', change);
    return () => media.removeEventListener?.('change', change);
  }, []);
  return reducedMotion;
}

function useHeroTail(reducedMotion) {
  const [tail, setTail] = useState(HERO_TAIL);
  useEffect(() => {
    if (reducedMotion) {
      setTail(HERO_TAIL);
      return undefined;
    }
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let interval = 0;
    const reveal = (progress) => {
      const locked = Math.floor(progress * HERO_TAIL.length);
      setTail(HERO_TAIL.split('').map((character, index) => {
        if (character === ' ' || index < locked) return character;
        return alphabet[(index * 13 + Math.floor(progress * 120)) % alphabet.length];
      }).join(''));
    };
    const start = window.setTimeout(() => {
      let step = 0;
      interval = window.setInterval(() => {
        step += 1;
        const progress = Math.min(1, step / 16);
        reveal(progress);
        if (progress === 1) {
          window.clearInterval(interval);
          setTail(HERO_TAIL);
        }
      }, 46);
    }, 260);
    const failSafe = window.setTimeout(() => {
      if (interval) window.clearInterval(interval);
      setTail(HERO_TAIL);
    }, 1450);
    return () => { window.clearTimeout(start); window.clearTimeout(failSafe); if (interval) window.clearInterval(interval); };
  }, [reducedMotion]);
  return tail;
}

function useBiomorphicStageMotion(rootRef, reducedMotion) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const stages = [...root.querySelectorAll('[data-bio-stage]')];
    if (reducedMotion || !('IntersectionObserver' in window)) {
      stages.forEach((stage) => stage.classList.add('is-visible'));
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) entry.target.classList.add('is-visible'); });
    }, { threshold: 0.18 });
    stages.forEach((stage) => observer.observe(stage));
    return () => observer.disconnect();
  }, [rootRef, reducedMotion]);
}

function useActiveBiomorphicStage(rootRef, reducedMotion) {
  const [activeId, setActiveId] = useState(biomorphicChapters[0][0]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const stages = [...root.querySelectorAll('[data-bio-stage]')];
    if (reducedMotion || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible?.target.id) setActiveId(visible.target.id);
    }, { rootMargin: '-38% 0px -45% 0px', threshold: [0.04, 0.2, 0.5] });
    stages.forEach((stage) => observer.observe(stage));
    return () => observer.disconnect();
  }, [rootRef, reducedMotion]);
  return activeId;
}

function useCreationVocabulary(reducedMotion) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const words = [...node.querySelectorAll('[data-vocabulary-word]')];
    if (reducedMotion) {
      node.classList.remove('is-scroll-controlled');
      words.forEach((word) => word.classList.add('is-active'));
      return undefined;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const stage = node.closest('[data-bio-stage]') || node;
      const rect = stage.getBoundingClientRect();
      const travel = Math.max(1, stage.offsetHeight - window.innerHeight);
      const progress = Math.min(1, Math.max(0, -rect.top / travel));
      const isInReadingRange = rect.top < window.innerHeight && rect.bottom > 0;
      const activeCount = isInReadingRange ? Math.max(1, Math.ceil(progress * creationWords.length)) : 0;
      words.forEach((word, index) => {
        word.classList.toggle('is-active', index < activeCount);
        word.classList.toggle('is-current', index === activeCount - 1);
        word.classList.toggle('is-pending', index >= activeCount);
      });
      node.classList.add('is-scroll-controlled');
    };
    const schedule = () => {
      if (frame) window.cancelAnimationFrame(frame);
      update();
      frame = window.requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, [reducedMotion]);
  return ref;
}

function useSalesChoreography(rootRef, reducedMotion) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const reveals = [...root.querySelectorAll('[data-sales-reveal]')];
    if (reducedMotion || !('IntersectionObserver' in window)) {
      reveals.forEach((node) => node.classList.add('is-in'));
      return undefined;
    }
    const ctx = gsap.context(() => {
      reveals.forEach((node) => {
        gsap.fromTo(node, { y: 32, opacity: 0.08 }, {
          y: 0,
          opacity: 1,
          duration: 0.95,
          ease: 'power3.out',
          scrollTrigger: { trigger: node, start: 'top 88%', toggleActions: 'play none none reverse' },
        });
      });
      const line = root.querySelector('[data-method-line]');
      if (line && line.getTotalLength) {
        const length = line.getTotalLength();
        gsap.set(line, { strokeDasharray: length, strokeDashoffset: length });
        gsap.to(line, {
          strokeDashoffset: 0,
          ease: 'none',
          scrollTrigger: { trigger: '#como-funciona', start: 'top 72%', end: 'bottom 35%', scrub: 0.55 },
        });
      }
      const seed = root.querySelector('[data-hero-seed]');
      if (seed) {
        gsap.to(seed, { y: -12, duration: 3.8, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      }
    }, root);
    return () => ctx.revert();
  }, [rootRef, reducedMotion]);
}

function StageBackdrop({ visual, priority = false }) {
  return <div className="bio-stage__scene" aria-hidden="true"><img src={visual.src} alt="" loading={priority ? 'eager' : 'lazy'} fetchPriority={priority ? 'high' : 'auto'} decoding="async" /><span className="bio-stage__veil" /><span className="bio-stage__progressive-blur" /><svg className="bio-stage__threads" viewBox="0 0 1000 700" preserveAspectRatio="none"><path d="M-20 562 C185 428 304 616 488 418 S761 238 1020 160" /><path d="M-20 630 C192 488 345 662 576 520 S803 338 1020 292" /></svg><span className="bio-stage__signal" /></div>;
}

function BioStage({ id, visual, className = '', priority = false, children }) {
  return <section id={id} className={`bio-stage ${className}`.trim()} data-bio-stage><StageBackdrop visual={visual} priority={priority} /><div className="bio-stage__content">{children}</div></section>;
}

function NarrativeRail({ activeId }) {
  return <nav className="bio-rail" aria-label="Percurso da investigação">{biomorphicChapters.map(([id, label]) => <a className={activeId === id ? 'is-active' : ''} href={`#${id}`} key={id}><span aria-hidden="true" /><span className="bio-rail__label">{label}</span></a>)}</nav>;
}

function BioSignalList() {
  const [activeId, setActiveId] = useState(lp1Signals[0].id);
  const active = lp1Signals.find((signal) => signal.id === activeId) || lp1Signals[0];
  return <div className="bio-signal-list" aria-label="Sinais investigados">
    <div className="bio-signal-list__tabs" role="tablist" aria-label="Escolha um sinal">
      {lp1Signals.map((signal) => <button
        className={signal.id === active.id ? 'is-active' : ''}
        id={`bio-signal-tab-${signal.id}`}
        key={signal.id}
        type="button"
        role="tab"
        aria-selected={signal.id === active.id}
        aria-controls={`bio-signal-panel-${signal.id}`}
        tabIndex={signal.id === active.id ? 0 : -1}
        onClick={() => setActiveId(signal.id)}
      ><span>{signal.number}</span><strong>{signal.label}</strong><Arrow /></button>)}
    </div>
    <article className="bio-signal-list__panel" id={`bio-signal-panel-${active.id}`} role="tabpanel" aria-labelledby={`bio-signal-tab-${active.id}`}>
      <div className="bio-signal-list__meta"><span>{active.number}</span><span>sinal em foco</span></div>
      <h3>{active.label}</h3>
      <p className="bio-signal-list__summary">{active.summary}</p>
      <p>{active.detail}</p>
      <a href="#metodo">Ver como isso entra no método <Arrow /></a>
    </article>
  </div>;
}

function PublicLadder({ tone = 'dark', visual = null }) {
  return (
    <section id="ofertas" className={`offer-ladder offer-ladder--${tone} ${visual ? 'offer-ladder--visual' : ''}`} aria-labelledby="offer-title" data-bio-stage={visual ? '' : undefined}>
      {visual ? <StageBackdrop visual={visual} /> : null}
      <div className="section-lead"><p className="section-kicker">Escolha a profundidade</p><h2 id="offer-title">A descoberta pode ser apenas o começo.</h2><p>Até onde você quer levar a ideia? Os três caminhos públicos aumentam a profundidade da decisão. O acompanhamento mais próximo acontece por aplicação.</p></div>
      {visual ? <div className="offer-card-grid" aria-label="Planos públicos da INÉDITA Labs">
        {publicOffers.map((offer, index) => <article className={`offer-card ${index === 1 ? 'offer-card--focus' : ''}`} key={offer.price}>
          <div className="offer-card__head"><span className="offer-card__type">{index === 0 ? 'Ponto de partida' : index === 1 ? 'Próxima decisão' : 'Conversa estratégica'}</span><span className="offer-card__price">{offer.price}</span></div>
          <h3>{offer.name}</h3>
          <p className="offer-card__copy">{offer.copy}</p>
          <div className="offer-card__rule" aria-hidden="true" />
          <p className="offer-card__includes-label">O que está incluído</p>
          <ul className="offer-card__includes">{offer.includes.map((item) => <li key={item}><CheckMark /><span>{item}</span></li>)}</ul>
          <a className="offer-card__cta" href={checkoutHref}>{offer.cta}<Arrow /></a>
        </article>)}
      </div> : <div className="offer-list">
        {publicOffers.map((offer, index) => <article className={`offer-row ${index === 1 ? 'offer-row--active' : ''}`} key={offer.price}>
          <span className="offer-row__index">0{index + 1}</span><div><h3>{offer.name}</h3><p>{offer.copy}</p></div><strong>{offer.price}</strong><a href={checkoutHref}>{offer.cta}<Arrow /></a>
        </article>)}
      </div>}
    </section>
  );
}

function BiomorphicPage() {
  const rootRef = useRef(null);
  const reducedMotion = useReducedMotionPreference();
  const vocabularyRef = useCreationVocabulary(reducedMotion);
  const activeId = useActiveBiomorphicStage(rootRef, reducedMotion);
  useSalesChoreography(rootRef, reducedMotion);
  return <main ref={rootRef} className={`page page--biomorphic sales-page ${reducedMotion ? '' : 'sales-page--motion'}`.trim()}>
    <header className="sales-header">
      <BrandLockup href="/biomorphic" />
      <nav className="sales-header__nav" aria-label="Seções da proposta">
        <a href="#metodo">O método</a>
        <a href="#como-funciona">Como funciona</a>
        <a href="#ofertas">Caminhos</a>
      </nav>
      <a className="sales-header__cta" href={startHref}>Começar</a>
    </header>

    <section id="biomorphic-hero" className="sales-hero" data-bio-stage>
      <div className="sales-hero__copy" data-sales-reveal>
        <h1 id="lp1-hero-title">A oportunidade que só faz sentido para você.</h1>
        <p className="sales-hero__promise">Criar ficou fácil. O Método INÉDITA Labs revela a direção escondida no que você sabe, viveu e consegue acessar — e mostra o que ainda precisa ser provado.</p>
        <div className="sales-hero__actions">
          <CTA href={startHref} tone="brass" magnetic>Descobrir minhas oportunidades</CTA>
          <a className="sales-text-link" href="#como-funciona">Ver como o trabalho acontece <Arrow /></a>
        </div>
      </div>
      <figure className="sales-hero__figure" data-hero-seed>
        <img src={salesAssets.hero.src} alt={salesAssets.hero.alt} width="1024" height="1024" fetchPriority="high" decoding="async" />
      </figure>
    </section>

    <section id="nova-escolha" className="sales-bottleneck" data-bio-stage>
      <div data-sales-reveal>
        <h2>Hoje quase qualquer pessoa consegue criar. Poucas sabem o que merece existir.</h2>
        <div className="sales-vocabulary" ref={vocabularyRef} aria-label={creationWords.join(', ')}>
          {creationWords.map((word) => <span data-vocabulary-word key={word}>{word}</span>)}
        </div>
        <p className="sales-turn">Se criar deixou de ser o problema, o novo gargalo está na decisão sobre o que merece existir.</p>
      </div>
    </section>

    <section id="metodo" className="sales-method" data-bio-stage>
      <div className="sales-method__copy" data-sales-reveal>
        <h2>O Método não inventa uma ideia. Ele encontra a que já estava na sua história.</h2>
        <p>Você não compra uma lista genérica. Descobre uma possibilidade que só fecha com a sua combinação de repertório, acesso e restrição — e entende por que ela apareceu.</p>
        <p>Não é um gerador de ideias. É um processo de descoberta.</p>
        <CTA href={startHref} tone="brass" magnetic>Começar minha investigação</CTA>
      </div>
      <figure className="sales-method__figure" data-sales-reveal>
        <img src={salesAssets.method.src} alt={salesAssets.method.alt} width="1024" height="1024" loading="lazy" decoding="async" />
      </figure>
    </section>

    <section id="como-funciona" className="sales-process" data-bio-stage aria-labelledby="process-title">
      <div className="sales-process__intro" data-sales-reveal>
        <h2 id="process-title">Como o trabalho acontece.</h2>
        <p>A proposta é uma investigação em seis movimentos. Cada etapa alimenta a seguinte. Nenhuma hipótese chega até você só porque parece interessante.</p>
      </div>
      <div className="sales-process__track">
        <svg className="sales-process__line" viewBox="0 0 8 1200" preserveAspectRatio="none" aria-hidden="true">
          <path data-method-line d="M4 0 V1200" />
        </svg>
        <ol className="sales-process__list">
          {methodStages.map((stage, index) => (
            <li data-sales-reveal key={stage.name}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <p className="sales-process__name">{stage.name}</p>
                <h3>{stage.title}</h3>
                <p>{stage.copy}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>

    <section id="investigacao" className="sales-questions" data-bio-stage>
      <div data-sales-reveal>
        <h2>O valor está nas perguntas que ninguém fez.</h2>
        <p>Talvez você não precise de melhores respostas. Talvez precise de perguntas que nunca fizeram para você. As próximas perguntas dependem, em parte, daquilo que as anteriores revelarem.</p>
      </div>
    </section>

    <section id="extracao" className="sales-signals" data-bio-stage>
      <div data-sales-reveal>
        <h2>Procuramos a combinação que outra pessoa não consegue copiar.</h2>
        <BioSignalList />
      </div>
    </section>

    <section id="entregaveis" className="sales-deliverables" data-bio-stage>
      <div data-sales-reveal>
        <h2>O que você leva.</h2>
        <p>A Descoberta cumpre sozinha a promessa vendida. Os caminhos seguintes aprofundam a mesma investigação, sem esconder o resultado para forçar um upgrade.</p>
        <ul className="sales-deliverables__grid">
          {['Investigação pelo Método INÉDITA', 'Cruzamento das respostas e aprofundamentos quando necessários', 'Passagem pelo Conselho INÉDITA', 'Possibilidades personalizadas, não uma lista genérica', 'Recomendação principal e alternativas relevantes', 'Racional de por que aquilo apareceu para você', 'Próximos passos iniciais para explorar a descoberta', 'Índice de Assimetria™ da combinação encontrada'].map((item) => <li key={item}><CheckMark /><span>{item}</span></li>)}
        </ul>
      </div>
    </section>

    <section id="ofertas" className="sales-offers" data-bio-stage aria-labelledby="offer-title">
      <div className="sales-offers__intro" data-sales-reveal>
        <h2 id="offer-title">Três profundidades. A mesma investigação.</h2>
        <p>Os três caminhos públicos aumentam a profundidade da decisão. O acompanhamento mais próximo acontece por aplicação.</p>
      </div>
      <div className="sales-offers__grid">
        {publicOffers.map((offer, index) => (
          <article className={`sales-offer ${index === 1 ? 'sales-offer--focus' : ''}`} data-sales-reveal key={offer.price}>
            <p className="sales-offer__price">{offer.price}</p>
            <h3>{offer.name}</h3>
            <p className="sales-offer__copy">{offer.copy}</p>
            <ul>{offer.includes.map((item) => <li key={item}><CheckMark /><span>{item}</span></li>)}</ul>
            <a className="sales-offer__cta" href={checkoutHref}>{offer.cta}<Arrow /></a>
          </article>
        ))}
      </div>
      <figure className="sales-offers__figure" data-sales-reveal>
        <img src={salesAssets.paths.src} alt={salesAssets.paths.alt} width="1024" height="1024" loading="lazy" decoding="async" />
      </figure>
    </section>

    <section id="compromissos" className="sales-commitments" data-bio-stage>
      <div data-sales-reveal>
        <h2>O que prometemos cumprir.</h2>
        <div className="sales-commitments__split">
          <article>
            <h3>Compromissos da proposta</h3>
            <ul>
              <li>A investigação acontece pelo Método INÉDITA, não por um pedido genérico de ideias.</li>
              <li>Antes de chegar até você, a hipótese precisa sobreviver ao Conselho.</li>
              <li>O caminho de R$297 entrega a descoberta comprada: recomendação, racional e próximos passos iniciais.</li>
              <li>Quando a evidência for insuficiente, o Método declara a limitação em vez de inventar certeza.</li>
            </ul>
          </article>
          <article>
            <h3>Limites honestos</h3>
            <ul>
              <li>Não há garantia de faturamento, clientes ou um negócio operando.</li>
              <li>Não há depoimento, métrica de conversão ou prova comercial inventada nesta página.</li>
              <li>Formatos de acompanhamento mais próximos dependem de aplicação, análise do projeto e disponibilidade.</li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <section id="faq" className="sales-faq" data-bio-stage>
      <div data-sales-reveal>
        <h2>Perguntas que costumam aparecer antes da decisão.</h2>
        <div className="sales-faq__list">
          {salesFaqs.map((item) => (
            <details key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <section id="acompanhamento" className="sales-close" data-bio-stage>
      <div data-sales-reveal>
        <h2>Algumas ideias pedem mais do que um plano.</h2>
        <p>Para determinados projetos, a INÉDITA Labs oferece formatos de acompanhamento mais próximos. O acesso acontece por aplicação. Vagas e formatos estão sujeitos à análise do projeto e à disponibilidade.</p>
        <div className="sales-hero__actions">
          <CTA href={startHref} tone="brass" magnetic>Descobrir minhas oportunidades</CTA>
          <a className="sales-text-link" href={applyHref}>Aplicar para acompanhamento <Arrow /></a>
        </div>
      </div>
    </section>

    <nav className="bio-rail" aria-label="Percurso da investigação">
      {biomorphicChapters.map(([id, label]) => <a className={activeId === id ? 'is-active' : ''} href={`#${id}`} key={id}><span aria-hidden="true" /><span className="bio-rail__label">{label}</span></a>)}
    </nav>
    <Footer variant="dark" />
  </main>;
}

const seededSignal = (index, offset = 0) => {
  const value = Math.sin((index + 1) * 12.9898 + (offset + 1) * 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const constrain = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const soften = (value, min = 0, max = 1) => {
  const t = constrain((value - min) / Math.max(.0001, max - min));
  return t * t * (3 - 2 * t);
};

const cinemaActs = [
  {
    point: 0,
    title: <>Uma oportunidade pode já estar <em>na sua empresa.</em></>,
    copy: 'Capacidades, ativos, acessos e problemas reais deixam pistas. Antes de investir, vale enxergar o que essas pistas podem estar dizendo juntas.',
  },
  {
    point: .29,
    title: <>A empresa já produz sinais que ainda <em>não se encontram.</em></>,
    copy: 'Eles aparecem em reuniões, rotinas, conversas com clientes e decisões aparentemente isoladas.',
  },
  {
    point: .58,
    title: <>Quando relações aparecem, uma hipótese deixa de ser <em>palpite.</em></>,
    copy: 'O Método INÉDITA aproxima o que estava disperso e mostra o que merece ser compreendido antes de avançar.',
  },
  {
    point: .86,
    title: <>A próxima decisão pode começar com <em>uma direção.</em></>,
    copy: 'Sem promessas automáticas. Com mais clareza sobre a realidade que sustenta o próximo experimento.',
    cta: 'Mapear uma oportunidade para minha empresa',
  },
];

const MORPH_PARTICLE_COUNT = 2400;
const particlePalette = ['235, 231, 219', '197, 168, 115', '134, 179, 155', '92, 142, 132', '150, 112, 136'];
const cinemaForms = ['cloud', 'orbit', 'bulb', 'arrow'];
const cinemaFormStops = cinemaActs.map(({ point }) => point);
const cinemaCameraYaw = { cloud: -.28, orbit: .72, bulb: .24, arrow: .04 };

const formCloud = ({ a, b, c }) => {
  const theta = a * Math.PI * 2;
  const phi = Math.acos(1 - b * 2);
  const lobe = .7 + c * .29 + Math.sin(theta * 3 + phi * 1.7) * .075;
  const radial = Math.sin(phi) * lobe;
  const split = Math.cos(theta) < 0 ? -.14 : .14;
  return {
    x: Math.cos(theta) * radial * 1.18 + split,
    y: Math.cos(phi) * lobe * .78,
    z: Math.sin(theta) * radial * .68,
  };
};

const formOrbit = ({ a, b, c }) => {
  const band = Math.min(2, Math.floor(a * 3));
  const angle = b * Math.PI * 2;
  const radius = .82 + (c - .5) * .12;
  const depth = (c - .5) * .22;
  if (band === 0) return { x: Math.cos(angle) * radius * 1.1, y: Math.sin(angle) * radius * .54, z: depth };
  if (band === 1) return { x: Math.cos(angle) * radius * .76, y: depth, z: Math.sin(angle) * radius * .76 };
  return { x: Math.cos(angle) * radius * .6, y: Math.sin(angle) * radius * .72, z: Math.sin(angle) * radius * .53 + depth };
};

const formBulb = ({ a, b, c }) => {
  const angle = b * Math.PI * 2;
  if (a < .72) {
    const t = a / .72;
    const radius = .07 + Math.sin(t * Math.PI * .92) * .7;
    return { x: Math.cos(angle) * radius, y: .62 - t * 1.28, z: Math.sin(angle) * radius * .62 };
  }
  if (a < .9) {
    const t = (a - .72) / .18;
    const radius = .25 - t * .06;
    return { x: Math.cos(angle) * radius, y: -.66 - t * .24, z: Math.sin(angle) * radius * .58 };
  }
  const row = (a - .9) / .1;
  return { x: (b - .5) * .54, y: -.91 - row * .16, z: (c - .5) * .22 };
};

const formArrow = ({ a, b, c }) => {
  if (a < .36) return { x: (b - .5) * .3, y: .74 - c * 1.34, z: (b - .5) * .28 };
  const root = Math.sqrt(b);
  const point = { x: 0, y: -1.08 };
  const left = { x: -.7, y: .08 };
  const right = { x: .7, y: .08 };
  return {
    x: (1 - root) * point.x + root * ((1 - c) * left.x + c * right.x),
    y: (1 - root) * point.y + root * ((1 - c) * left.y + c * right.y),
    z: (a - .68) * .42,
  };
};

const cinemaParticles = Array.from({ length: MORPH_PARTICLE_COUNT }, (_, index) => {
  const a = seededSignal(index, 1);
  const b = seededSignal(index, 2);
  const c = seededSignal(index, 3);
  return {
    id: index,
    a,
    b,
    c,
    ambient: index % 11 === 0,
    phase: seededSignal(index, 4) * Math.PI * 2,
    size: .5 + seededSignal(index, 5) * 1.55,
    colorIndex: Math.min(particlePalette.length - 1, Math.floor(seededSignal(index, 6) * particlePalette.length)),
    scatter: {
      x: (seededSignal(index, 7) * 2 - 1) * 1.72,
      y: (seededSignal(index, 8) * 2 - 1) * 1.18,
      z: (seededSignal(index, 9) * 2 - 1) * 1.34,
    },
    forms: {
      cloud: formCloud({ a, b, c }),
      orbit: formOrbit({ a, b, c }),
      bulb: formBulb({ a, b, c }),
      arrow: formArrow({ a, b, c }),
    },
  };
});

const morphScene = (progress) => {
  const value = constrain(progress);
  const index = cinemaFormStops.reduce((lastIndex, stop, currentIndex) => (value >= stop ? currentIndex : lastIndex), 0);
  if (index === cinemaForms.length - 1) return { from: cinemaForms[index], to: cinemaForms[index], mix: 0, dissolve: 0 };
  const start = cinemaFormStops[index];
  const end = cinemaFormStops[index + 1];
  const mix = soften(constrain((value - start) / (end - start)));
  return {
    from: cinemaForms[index],
    to: cinemaForms[index + 1],
    mix,
    dissolve: Math.sin(mix * Math.PI) ** 2,
  };
};

function useCinematicProgress(stageRef, reducedMotion) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (reducedMotion) {
      setProgress(1);
      return undefined;
    }
    let frame = 0;
    let lastProgress = -1;
    const update = () => {
      frame = 0;
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const range = Math.max(1, stage.offsetHeight - window.innerHeight);
      const next = constrain(-rect.top / range);
      if (Math.abs(next - lastProgress) > .0015) {
        lastProgress = next;
        setProgress(next);
      }
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [stageRef, reducedMotion]);

  return progress;
}

function CinematicSignalField({ progress, reducedMotion }) {
  const hostRef = useRef(null);
  const canvasRef = useRef(null);
  const progressRef = useRef(progress);
  const scene = morphScene(reducedMotion ? 1 : progress);

  useEffect(() => { progressRef.current = progress; }, [progress]);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d', { alpha: true });
    if (!host || !canvas || !context) return undefined;

    let width = 0;
    let height = 0;
    let frame = 0;
    let isIntersecting = true;
    let deviceRatio = 1;
    let lastDraw = 0;
    const pointer = { x: 0, y: 0 };
    const lowPower = Number(navigator.deviceMemory || 8) < 4 || Number(navigator.hardwareConcurrency || 8) < 4;

    const rotate = (particle, yaw, pitch) => {
      const cosineYaw = Math.cos(yaw);
      const sineYaw = Math.sin(yaw);
      const x = particle.x * cosineYaw - particle.z * sineYaw;
      const z = particle.x * sineYaw + particle.z * cosineYaw;
      const cosinePitch = Math.cos(pitch);
      const sinePitch = Math.sin(pitch);
      return {
        x,
        y: particle.y * cosinePitch - z * sinePitch,
        z: particle.y * sinePitch + z * cosinePitch,
      };
    };

    const appendShard = (point) => {
      const { x, y, size, turn } = point;
      const top = turn - Math.PI / 2;
      const right = turn + Math.PI / 6;
      const left = turn + Math.PI * 5 / 6;
      context.moveTo(x + Math.cos(top) * size * 1.32, y + Math.sin(top) * size * 1.32);
      context.lineTo(x + Math.cos(right) * size, y + Math.sin(right) * size);
      context.lineTo(x + Math.cos(left) * size, y + Math.sin(left) * size);
      context.closePath();
    };

    const draw = (time = 0) => {
      if (!width || !height) return;
      context.clearRect(0, 0, width, height);
      const sceneProgress = reducedMotion ? 1 : progressRef.current;
      const morph = morphScene(sceneProgress);
      const compactViewport = width < 760;
      const activeParticles = cinemaParticles.slice(0, lowPower ? 720 : compactViewport ? 1800 : cinemaParticles.length);
      const yaw = cinemaCameraYaw[morph.from] + (cinemaCameraYaw[morph.to] - cinemaCameraYaw[morph.from]) * morph.mix + time * .000072 + pointer.x * .22;
      const pitch = -.08 + Math.sin(time * .000055) * .07 + pointer.y * .13;
      const distance = 4.56 - sceneProgress * .42;
      const scale = Math.min(width, height) / (compactViewport ? 390 : 540);
      const centerX = width * (compactViewport ? .65 : .665);
      const centerY = height * (compactViewport ? .43 : .48);
      const batches = particlePalette.map(() => [[], [], []]);

      activeParticles.forEach((particle) => {
        let positioned;
        if (particle.ambient) {
          const drift = reducedMotion ? 0 : Math.sin(time * .00014 + particle.phase) * .04;
          positioned = { x: particle.scatter.x * 1.34 + drift, y: particle.scatter.y * 1.22, z: particle.scatter.z };
        } else {
          const from = particle.forms[morph.from];
          const to = particle.forms[morph.to];
          const drift = reducedMotion ? 0 : Math.sin(time * .00021 + particle.phase) * .018;
          positioned = {
            x: from.x + (to.x - from.x) * morph.mix + particle.scatter.x * morph.dissolve * .58 + drift,
            y: from.y + (to.y - from.y) * morph.mix + particle.scatter.y * morph.dissolve * .42 + Math.cos(time * .00017 + particle.phase) * (reducedMotion ? 0 : .014),
            z: from.z + (to.z - from.z) * morph.mix + particle.scatter.z * morph.dissolve * .5,
          };
        }
        const rotated = rotate(positioned, yaw, pitch);
        const perspective = distance / (distance - rotated.z);
        const depth = constrain((rotated.z + 1.8) / 3.6);
        const alpha = particle.ambient ? .14 + depth * .2 : compactViewport ? .42 + depth * .78 : .24 + depth * .72;
        const alphaBucket = alpha > .68 ? 2 : alpha > .41 ? 1 : 0;
        batches[particle.colorIndex][alphaBucket].push({
          x: centerX + rotated.x * width * .31 * perspective,
          y: centerY + rotated.y * height * .46 * perspective,
          size: Math.max(.48, particle.size * (1.05 + depth * 1.9) * scale * perspective),
          turn: particle.phase + time * .00025 + rotated.z * .42,
        });
      });

      const halo = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, Math.max(width, height) * .54);
      halo.addColorStop(0, `rgba(51, 92, 72, ${.075 + (1 - morph.dissolve) * .13})`);
      halo.addColorStop(.5, 'rgba(17, 45, 33, .025)');
      halo.addColorStop(1, 'rgba(7, 16, 13, 0)');
      context.fillStyle = halo;
      context.fillRect(0, 0, width, height);

      context.save();
      context.globalCompositeOperation = 'lighter';
      const alphaLevels = compactViewport ? [.38, .7, 1] : [.27, .56, .92];
      particlePalette.forEach((color, colorIndex) => {
        batches[colorIndex].forEach((bucket, alphaIndex) => {
          if (!bucket.length) return;
          context.beginPath();
          bucket.forEach(appendShard);
          context.globalAlpha = alphaLevels[alphaIndex];
          context.fillStyle = `rgb(${color})`;
          context.fill();
        });
      });
      context.restore();
    };

    const stop = () => {
      if (!frame) return;
      window.cancelAnimationFrame(frame);
      frame = 0;
    };

    const render = (time) => {
      frame = 0;
      if (lowPower && time - lastDraw < 32) {
        frame = window.requestAnimationFrame(render);
        return;
      }
      lastDraw = time;
      draw(time);
      if (isIntersecting && !document.hidden && !reducedMotion) frame = window.requestAnimationFrame(render);
    };

    const start = () => {
      if (!isIntersecting || document.hidden) return;
      if (reducedMotion) {
        draw(0);
        return;
      }
      if (!frame) frame = window.requestAnimationFrame(render);
    };

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = Math.max(1, Math.round(bounds.width));
      height = Math.max(1, Math.round(bounds.height));
      deviceRatio = Math.min(window.devicePixelRatio || 1, lowPower ? 1.2 : 1.5);
      canvas.width = Math.round(width * deviceRatio);
      canvas.height = Math.round(height * deviceRatio);
      context.setTransform(deviceRatio, 0, 0, deviceRatio, 0, 0);
      draw(0);
    };

    const onPointerMove = (event) => {
      if (reducedMotion) return;
      const bounds = host.getBoundingClientRect();
      pointer.x = ((event.clientX - bounds.left) / bounds.width - .5) * 2;
      pointer.y = ((event.clientY - bounds.top) / bounds.height - .5) * 2;
    };
    const resetPointer = () => { pointer.x = 0; pointer.y = 0; };
    const onVisibilityChange = () => { if (document.hidden) stop(); else start(); };
    const intersectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(([entry]) => {
      isIntersecting = entry.isIntersecting;
      if (isIntersecting) start(); else stop();
    }, { threshold: .12 }) : null;
    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(resize) : null;

    resize();
    intersectionObserver?.observe(host);
    resizeObserver?.observe(canvas);
    if (!resizeObserver) window.addEventListener('resize', resize);
    host.addEventListener('pointermove', onPointerMove);
    host.addEventListener('pointerleave', resetPointer);
    document.addEventListener('visibilitychange', onVisibilityChange);
    start();

    return () => {
      stop();
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', resize);
      host.removeEventListener('pointermove', onPointerMove);
      host.removeEventListener('pointerleave', resetPointer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [reducedMotion]);

  return <div ref={hostRef} className="business-cinema__field" data-particle-count={MORPH_PARTICLE_COUNT} data-morph-scene={scene.mix < .5 ? scene.from : scene.to} role="img" aria-label="Campo tridimensional com 2.400 partículas. Pelo scroll, elas formam uma nuvem, órbitas, uma lâmpada e uma seta de direção.">
    <canvas ref={canvasRef} className="business-cinema__canvas" aria-hidden="true" />
  </div>;
}

function BusinessOfferLadder() {
  return <section className="business-offers" aria-labelledby="business-offers-title">
    <div className="business-offers__lead"><h2 id="business-offers-title">A descoberta ganha a profundidade que a próxima decisão pede.</h2><p>Os três caminhos públicos aumentam a resolução da investigação. O acompanhamento mais próximo acontece por aplicação.</p></div>
    <div className="business-offers__list">{publicOffers.map((offer) => <article className="business-offer" key={offer.price}><strong>{offer.price}</strong><div><h3>{offer.name}</h3><p>{offer.copy}</p></div><a href={checkoutHref}>{offer.cta}<Arrow /></a></article>)}</div>
  </section>;
}

function BusinessPage() {
  const stageRef = useRef(null);
  const reducedMotion = useReducedMotionPreference();
  const progress = useCinematicProgress(stageRef, reducedMotion);
  const activeActIndex = cinemaActs.reduce((nearestIndex, act, index) => (
    Math.abs(progress - act.point) < Math.abs(progress - cinemaActs[nearestIndex].point) ? index : nearestIndex
  ), 0);

  return <main className="page page--business">
    <Header variant="dark" publicSurface brandHref="/" />
    <section ref={stageRef} className="business-cinema" aria-labelledby="business-title">
      <div className="business-cinema__sticky">
        <CinematicSignalField progress={progress} reducedMotion={reducedMotion} />
        <div className="business-cinema__veil" aria-hidden="true" />
        <div className="business-cinema__narrative">
          {cinemaActs.map((act, index) => {
            const isDominant = reducedMotion || index === activeActIndex;
            const opacity = isDominant ? 1 : 0;
            const offset = reducedMotion || isDominant ? 0 : (index < activeActIndex ? -24 : 24);
            const Heading = index === 0 ? 'h1' : 'h2';
            return <article className={`business-cinema__act ${isDominant ? 'is-dominant' : ''}`} key={act.point} style={{ '--cinema-opacity': opacity, '--cinema-offset': `${offset}px` }}>
              <Heading id={index === 0 ? 'business-title' : undefined}>{act.title}</Heading>
              <p>{act.copy}</p>
              {act.cta ? <CTA href={startHref} tone="brass">{act.cta}</CTA> : null}
            </article>;
          })}
        </div>
        <div className="business-cinema__progress" aria-hidden="true"><span style={{ transform: `scaleX(${Math.max(.025, progress)})` }} /></div>
        <p className="business-cinema__cue" aria-hidden="true">Desça para atravessar o campo.</p>
      </div>
    </section>
    <section className="business-bridge" aria-labelledby="business-bridge-title"><div><h2 id="business-bridge-title">Uma hipótese não elimina a incerteza.</h2><p>Ela torna a próxima conversa mais específica: o que investigar, quem ouvir e qual teste realmente faz sentido.</p></div><a className="inline-link inline-link--brass" href={startHref}>Começar pelo mapa da oportunidade <Arrow /></a></section>
    <BusinessOfferLadder />
    <section className="business-application"><div><h2>Alguns projetos avançam por aplicação.</h2><p>Se a oportunidade exigir maior participação humana, análise de contexto ou execução acompanhada, o próximo passo é uma aplicação.</p></div><CTA href={applyHref} tone="brass">Aplicar para acompanhamento</CTA></section>
    <Footer variant="dark" />
  </main>;
}

const journeyChapters = [
  ['01', 'O sinal aparece', 'Uma oportunidade começa como fragmento.'],
  ['02', 'A pergunta muda o mapa', 'A história deixa de ser contexto e passa a ser evidência.'],
  ['03', 'As conexões ganham forma', 'Capacidades, acessos e problemas se aproximam.'],
  ['04', 'A hipótese é pressionada', 'O que parece fascinante precisa encontrar a realidade.'],
  ['05', 'Uma direção se destaca', 'Poucas possibilidades chegam com razão suficiente para continuar.'],
  ['06', 'A decisão começa', 'Você escolhe até onde levar a descoberta.'],
];

function useScrollVideo(stageRef, videoRef, reducedMotion) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (reducedMotion) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const stage = stageRef.current;
      const video = videoRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      const range = Math.max(1, stage.offsetHeight - window.innerHeight);
      const next = Math.min(1, Math.max(0, -rect.top / range));
      setProgress(next);
      if (video?.readyState >= 1 && Number.isFinite(video.duration) && video.duration > 0) {
        const target = Math.min(video.duration - 0.05, Math.max(0, next * video.duration));
        if (Math.abs(video.currentTime - target) > 0.035) video.currentTime = target;
      }
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    videoRef.current?.addEventListener('loadedmetadata', schedule);
    return () => { window.removeEventListener('scroll', schedule); window.removeEventListener('resize', schedule); videoRef.current?.removeEventListener('loadedmetadata', schedule); if (frame) window.cancelAnimationFrame(frame); };
  }, [stageRef, videoRef, reducedMotion]);
  return progress;
}

function JourneyPage() {
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const progress = useScrollVideo(stageRef, videoRef, reducedMotion);
  const active = Math.min(journeyChapters.length - 1, Math.floor(progress * journeyChapters.length));
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = (event) => setReducedMotion(event.matches);
    media.addEventListener?.('change', change);
    return () => media.removeEventListener?.('change', change);
  }, []);
  return <main className="page page--journey">
    <Header variant="dark" label="Variação 03 / Jornada" />
    <section className="scrub-stage" ref={stageRef} aria-labelledby="scrub-title">
      <div className="scrub-sticky"><div className="scrub-media" aria-hidden="true"><video ref={videoRef} poster={journeyPosterSrc || undefined} muted playsInline preload={reducedMotion || !journeyVideoSrc ? 'none' : 'metadata'}>{!reducedMotion && journeyVideoSrc ? <source src={journeyVideoSrc} type="video/mp4" /> : null}</video><span /></div><div className="scrub-copy"><p className="section-kicker">Uma jornada para quem ainda não tem uma ideia</p><h1 id="scrub-title">Você não precisa começar com uma ideia.</h1><p>Comece entendendo o que já existe em você e ao seu redor.</p><CTA href={startHref}>Começar a investigação</CTA></div><div className="scrub-progress"><span style={{ transform: `scaleX(${progress})` }} /></div><div className="scrub-chapters" aria-live="polite">{journeyChapters.map(([number, title, copy], index) => <article className={index === active ? 'is-active' : ''} key={number}><span>{number}</span><div><h2>{title}</h2><p>{copy}</p></div></article>)}</div><span className="scrub-instruction">{reducedMotion ? 'Movimento reduzido: leitura completa disponível' : journeyVideoSrc ? 'Role para atravessar a jornada' : 'Vídeo da jornada aguardando integração'}</span></div>
    </section>
    <section className="journey-after"><p className="section-kicker">O que acontece depois do percurso</p><h2>As perguntas organizam aquilo que ainda estava sem nome.</h2><div className="journey-after__grid"><p>A INÉDITA cruza sua história, capacidades, acessos, recursos, preferências e restrições.</p><p>As possibilidades são comparadas, questionadas e confrontadas com a realidade antes de uma recomendação aparecer.</p><p>O resultado explica por que aquela oportunidade faz sentido para você e quais caminhos podem seguir.</p></div><CTA href={startHref} tone="light">Descobrir minhas oportunidades</CTA></section>
    <PublicLadder tone="dark" />
    <section className="application-bridge"><div><p className="section-kicker">Depois da descoberta</p><h2>Se o próximo passo pedir companhia, existe uma aplicação.</h2><p>O acompanhamento mais próximo depende da análise do projeto e da disponibilidade.</p></div><CTA href={applyHref} tone="light">Aplicar para acompanhamento</CTA></section>
    <Footer variant="dark" />
  </main>;
}

function LabIndex() {
  const variants = [
    { href: '/biomorphic', label: '01 / Biomorphic', title: 'A investigação como objeto editorial.', copy: 'Paleta mineral, imagens BIOMORPHIC e uma leitura sobre o valor latente.', tone: 'bio' },
    { href: '/business', label: '02 / Empresas', title: 'Oportunidades para negócios que precisam decidir.', copy: 'Uma superfície empresarial para capacidades internas, ativos e problemas reais.', tone: 'business' },
    { href: '/journey', label: '03 / Jornada', title: 'A ideia aparece enquanto você atravessa.', copy: 'Uma jornada scroll scrub com vídeo de fundo e capítulos progressivos.', tone: 'journey' },
  ];
  return <main className="lab-index"><Header variant="dark" label="Subprojeto INEDITA" /><section className="lab-index__hero"><p className="section-kicker">inedita-landing-lab / três superfícies</p><h1>O mesmo método.<br /><em>Três formas de entrar.</em></h1><p>Variações para testar a primeira impressão sem perder a verdade do produto: descobrir, estruturar e decidir.</p></section><section className="variant-grid" aria-label="Variações disponíveis">{variants.map((variant) => <a className={`variant-tile variant-tile--${variant.tone}`} href={variant.href} key={variant.href}><span>{variant.label}</span><h2>{variant.title}</h2><p>{variant.copy}</p><b>Ver variação <Arrow /></b></a>)}</section><Footer variant="dark" /></main>;
}

function App() {
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  const standaloneEntry = String(import.meta.env.VITE_INEDITA_ROOT_ENTRY || '').trim();
  const standalonePages = {
    biomorphic: <BiomorphicPage />,
    business: <BusinessPage />,
    journey: <JourneyPage />,
  };

  if (path === '/' && standalonePages[standaloneEntry]) return standalonePages[standaloneEntry];
  if (path === '/biomorphic') return <BiomorphicPage />;
  if (path === '/business') return <BusinessPage />;
  if (path === '/journey') return <JourneyPage />;
  return <LabIndex />;
}

createRoot(document.getElementById('root')).render(<App />);
